import { type ComponentType, lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router";

import { AppShell, PageLoading } from "@/components/app-shell";
import { HomeRedirect, ProtectedRoute, SalesWorkRoute } from "@/components/protected-route";
import { AddinPage } from "@/features/addin";
import { CaseReviewPage } from "@/features/case-review";
import { GiveFeedbackProvider } from "@/features/feedback";
import { LoginPage } from "@/features/login";
import { NotFoundPage } from "@/features/not-found";
import { QueuePage } from "@/features/queue";
import { BACK_FOR_YOU_HREF } from "@/features/queue/queue-defaults";
import { FEATURES } from "@/lib/features";
import { logger } from "@/lib/logger";

/*
 * Routes on demand (ease round, 13 Oct 2026). The first script file holds what a sales
 * person needs on the way to a case: the login, the shell, the queue, the case page and
 * the add-in pane. Every other page is fetched when its route opens. Each import names
 * the page's own file, not the index of the feature, so the page is one file of its own.
 * `vite.config.ts` marks the feature indexes as re-exports only: the queue and the case
 * page import small parts through them (the help button, a "why" link) and the pages
 * stay out of the first file.
 */

/** Where the time of the last reload for a new build is kept (this tab only). */
export const PAGE_RELOAD_KEY = "i2q.page.reloaded";
/** A second missing page file inside this time is an error, not a reason to reload again. */
export const PAGE_RELOAD_GUARD_MS = 60_000;

/** True when a missing page file may reload the tab: no reload in the last minute. */
export function mayReloadForNewBuild(lastReloadAt: number, now: number): boolean {
  return !(lastReloadAt > 0 && now - lastReloadAt < PAGE_RELOAD_GUARD_MS);
}

/**
 * A page file that does not load. After a deploy an open tab still names the files of
 * the old build, and the server answers 404 for them. The tab then reloads once and gets
 * the new build at the same address. Without session storage there is no guard against a
 * loop, so the error is passed on.
 */
function reloadForNewBuild(error: unknown): Promise<never> {
  let last = 0;
  try {
    last = Number(window.sessionStorage.getItem(PAGE_RELOAD_KEY)) || 0;
    if (mayReloadForNewBuild(last, Date.now())) {
      window.sessionStorage.setItem(PAGE_RELOAD_KEY, String(Date.now()));
      logger.warn("[app] a page file did not load, reloading for the new build", error);
      window.location.reload();
      // The tab is on its way out: the page keeps its loading state until then.
      return new Promise<never>(() => {});
    }
  } catch (storageError) {
    logger.warn("[app] no session storage, no reload", storageError);
  }
  throw error;
}

/** One page that loads on demand: its file, and the named component in it. */
function onDemand<M>(load: () => Promise<M>, pick: (module: M) => ComponentType) {
  return lazy(() => load().then((module) => ({ default: pick(module) }), reloadForNewBuild));
}

const CostPage = onDemand(
  () => import("@/features/cost/cost-page"),
  (m) => m.CostPage,
);
const DashboardPage = onDemand(
  () => import("@/features/dashboard/dashboard"),
  (m) => m.DashboardPage,
);
const ReportPage = onDemand(
  () => import("@/features/dashboard/report"),
  (m) => m.ReportPage,
);
const DecisionsPage = onDemand(
  () => import("@/features/discussions/decisions-page"),
  (m) => m.DecisionsPage,
);
const DiscussionDetailPage = onDemand(
  () => import("@/features/discussions/discussion-detail"),
  (m) => m.DiscussionDetailPage,
);
const DiscussionsPage = onDemand(
  () => import("@/features/discussions/discussions-page"),
  (m) => m.DiscussionsPage,
);
const DocsPage = onDemand(
  () => import("@/features/docs/docs-page"),
  (m) => m.DocsPage,
);
const FeedbackDetailPage = onDemand(
  () => import("@/features/feedback/feedback-detail"),
  (m) => m.FeedbackDetailPage,
);
const FeedbackTriagePage = onDemand(
  () => import("@/features/feedback/feedback-triage"),
  (m) => m.FeedbackTriagePage,
);
const MyFeedbackPage = onDemand(
  () => import("@/features/feedback/my-feedback"),
  (m) => m.MyFeedbackPage,
);
const HelpPage = onDemand(
  () => import("@/features/help/help"),
  (m) => m.HelpPage,
);
const LearnedPage = onDemand(
  () => import("@/features/learned/learned"),
  (m) => m.LearnedPage,
);
const ExtractionTrainingPage = onDemand(
  () => import("@/features/extraction-training/extraction-training-page"),
  (m) => m.ExtractionTrainingPage,
);
const LearnFromSalesPage = onDemand(
  () => import("@/features/learn-from-sales/learn-from-sales-page"),
  (m) => m.LearnFromSalesPage,
);
const MasterDataPage = onDemand(
  () => import("@/features/master-data/master-data-page"),
  (m) => m.MasterDataPage,
);
const PlatformPage = onDemand(
  () => import("@/features/platform/platform-page"),
  (m) => m.PlatformPage,
);
const ProcessMiningCasePage = onDemand(
  () => import("@/features/process-mining/case-timeline"),
  (m) => m.ProcessMiningCasePage,
);
const ProcessMiningPage = onDemand(
  () => import("@/features/process-mining/process-mining-page"),
  (m) => m.ProcessMiningPage,
);
const RoadmapPage = onDemand(
  () => import("@/features/roadmap/roadmap"),
  (m) => m.RoadmapPage,
);
const CountryGuidePage = onDemand(
  () => import("@/features/rules/country-guide"),
  (m) => m.CountryGuidePage,
);
const CountryIndex = onDemand(
  () => import("@/features/rules/country-index"),
  (m) => m.CountryIndex,
);
const DefaultGuideRedirect = onDemand(
  () => import("@/features/rules/rules-layout"),
  (m) => m.DefaultGuideRedirect,
);
const GlossaryPage = onDemand(
  () => import("@/features/rules/glossary"),
  (m) => m.GlossaryPage,
);
const HowItWorksPage = onDemand(
  () => import("@/features/rules/how-it-works"),
  (m) => m.HowItWorksPage,
);
const LiveSettingsPage = onDemand(
  () => import("@/features/rules/live-settings"),
  (m) => m.LiveSettingsPage,
);
const RulesLayout = onDemand(
  () => import("@/features/rules/rules-layout"),
  (m) => m.RulesLayout,
);
const QuickStartPage = onDemand(
  () => import("@/features/training/quick-start"),
  (m) => m.QuickStartPage,
);
const TrainingManagersPage = onDemand(
  () => import("@/features/training/managers-page"),
  (m) => m.TrainingManagersPage,
);
const TrainingModulePage = onDemand(
  () => import("@/features/training/player/player"),
  (m) => m.TrainingModulePage,
);
const TrainingPage = onDemand(
  () => import("@/features/training/training-page"),
  (m) => m.TrainingPage,
);
const TrainingTeamPage = onDemand(
  () => import("@/features/training/team/team-page"),
  (m) => m.TrainingTeamPage,
);
const UsagePage = onDemand(
  () => import("@/features/usage/usage-page"),
  (m) => m.UsagePage,
);

export function App() {
  return (
    // The pages of the shell wait inside the shell (`app-shell.tsx`); this boundary is
    // for the one lazy page outside it, the printed report.
    <Suspense fallback={<PageLoading />}>
      <AppRoutes />
    </Suspense>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<ProtectedRoute />}>
        {/* The queue, or the dashboard Summary for an executive-only session. */}
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/home" element={<HomeRedirect />} />
        {/* The monthly report prints on its own: no sidebar or header (A4, light theme). */}
        <Route path="/dashboard/report" element={<ReportPage />} />
        {/* One "Give feedback" dialog for every page of the shell (docs/feedback.md). */}
        <Route
          element={
            <GiveFeedbackProvider>
              <AppShell />
            </GiveFeedbackProvider>
          }
        >
          {/* The sales work: an executive-only session is sent to the dashboard Summary. */}
          <Route element={<SalesWorkRoute />}>
            <Route path="/queue" element={<QueuePage />} />
            {/* A short link to the cases back for the viewer (any received date). */}
            <Route path="/back" element={<Navigate to={BACK_FOR_YOU_HREF} replace />} />
            {/* /case without an id shows "No case selected"; /case/:id opens a stored case. */}
            <Route path="/case" element={<CaseReviewPage />} />
            <Route path="/case/:id" element={<CaseReviewPage />} />
            <Route path="/addin" element={<AddinPage />} />
            <Route path="/learned" element={<LearnedPage />} />
            {/* Learn from sales (20 Oct 2026): managers and admins (capability
                see_sales_truth); the page and the server gate it. Closed to an
                executive-only session. */}
            <Route path="/learn-from-sales" element={<LearnFromSalesPage />} />
            {/* Extraction training (4 Nov 2026): the same capability see_sales_truth; the
                page and the server gate it. No menu entry yet: it is reached by its link.
                The entry goes into "Insight" of app-shell.tsx when the product owner
                shows the page to more people. Closed to an executive-only session. */}
            <Route path="/extraction-training" element={<ExtractionTrainingPage />} />
            {FEATURES.training && (
              <>
                <Route path="/training" element={<TrainingPage />} />
                <Route path="/training/quick-start" element={<QuickStartPage />} />
                <Route path="/training/team" element={<TrainingTeamPage />} />
                <Route path="/training/managers" element={<TrainingManagersPage />} />
                <Route path="/training/:moduleId" element={<TrainingModulePage />} />
              </>
            )}
            {/* Rules and countries: read only. "live" is for managers and admins
                (capability see_rules); the tab and the server gate it. */}
            <Route path="/rules" element={<RulesLayout />}>
              <Route index element={<DefaultGuideRedirect />} />
              <Route path="countries" element={<CountryIndex />} />
              <Route path="glossary" element={<GlossaryPage />} />
              <Route path="how-it-works" element={<HowItWorksPage />} />
              <Route path="live" element={<LiveSettingsPage />} />
              <Route path=":guide" element={<CountryGuidePage />} />
            </Route>
            <Route path="/feedback/triage" element={<FeedbackTriagePage />} />
            {/* The usage report: admins only (capability see_usage, 15 Oct 2026); the page
                and the server gate it. Closed to an executive-only session. */}
            <Route path="/usage" element={<UsagePage />} />
            {/* Master data (15 Oct 2026): the defects that hurt cases. Every signed-in
                person sees and marks those of the own areas; closed to an executive-only
                session (inside SalesWorkRoute, and the server answers 403). */}
            <Route path="/master-data" element={<MasterDataPage />} />
            {/* Process mining (15 Oct 2026): how a quotation is made today, before I2Q.
                Only the role processmining (capability see_process_mining); the page and
                the server gate it. Closed to an executive-only session. */}
            <Route path="/process-mining" element={<ProcessMiningPage />} />
            <Route path="/process-mining/cases/:caseKey" element={<ProcessMiningCasePage />} />
          </Route>
          <Route path="/dashboard" element={<DashboardPage />} />
          {/* Cost and runs (14 Oct 2026): admins only since 15 Oct 2026 (capability
              see_finops); the page and the server gate it. */}
          <Route path="/cost" element={<CostPage />} />
          {/* What runs today (15 Oct 2026): every signed-in person, scoped to the own
              areas. Outside SalesWorkRoute, so an executive reaches it. */}
          <Route path="/platform" element={<PlatformPage />} />
          <Route path="/help" element={<HelpPage />} />
          {/* The developer documentation: for everyone signed in, every role. */}
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/feedback" element={<MyFeedbackPage />} />
          <Route path="/feedback/:id" element={<FeedbackDetailPage />} />
          {/* Open questions and decisions of record, and what is live or planned: for
              everyone signed in, an executive-only session included. */}
          <Route path="/discussions" element={<DiscussionsPage />} />
          <Route path="/discussions/:id" element={<DiscussionDetailPage />} />
          {/* The business decisions by milestone (15 Oct 2026): for everyone signed in; a
              decision opens at /discussions/:id. */}
          <Route path="/decisions" element={<DecisionsPage />} />
          <Route path="/roadmap" element={<RoadmapPage />} />
        </Route>
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
